import React, {useEffect} from 'react';
import {connect} from "react-redux";
import {Link} from "react-router-dom";
import {withTranslation} from "react-i18next";
import Footer from "../core/layout/footer/footer";
import './aiCapabilities.css';

/* ─── Section data ───────────────────────────── */
const heroSteps = [
    {num: '01', text: 'Problem-first, always',           sub: 'We understand your challenge before suggesting any solution'},
    {num: '02', text: 'Honest about what AI can do',     sub: "If it's not the right tool, we'll say so upfront"},
    {num: '03', text: 'Built for production, not demos', sub: 'Real data, real systems, real constraints — from day one'},
    {num: '04', text: 'We grow with you',                sub: 'One project reveals the next — we\'re a long-term AI partner'},
];

const pillars = [
    {
        icon: 'fa-align-left', delay: '',   title: 'Text & LLM Intelligence',
        desc: 'Large language models to extract structure from unstructured data, automate schema inference, and power knowledge-grounded conversational interfaces.',
        tags: ['LLM Pipelines', 'RAG', 'Schema Inference', 'LangChain'],
    },
    {
        icon: 'fa-eye', delay: 'd1', title: 'Vision & Document AI',
        desc: 'Extract, structure, and act on information locked inside documents — PDFs, scanned forms, KYC records, patents — with pipelines accurate enough for production.',
        tags: ['OCR', 'Google Vision', 'Document Parsing', 'PDF Intelligence'],
    },
    {
        icon: 'fa-microphone-alt', delay: 'd2', title: 'Speech & Voice AI',
        desc: 'Convert spoken language into structured data, actions, and records — from meeting capture to voice-first interfaces — with support for multilingual contexts.',
        tags: ['Speech-to-Text', 'Voice Interfaces', 'Meeting AI', 'Multilingual'],
    },
    {
        icon: 'fa-video', delay: 'd3', title: 'Video Intelligence',
        desc: 'Analyse video streams and recorded footage to detect objects, track movements, flag anomalies, and generate event-based alerts — turning cameras into intelligent sensors.',
        tags: ['Object Detection', 'Anomaly Detection', 'Video Processing', 'Event Alerts'],
    },
    {
        icon: 'fa-database', delay: 'd4', title: 'Data & Analytics AI',
        desc: 'AI-powered transformation, classification, and insight generation from structured and semi-structured business data — making your existing data actually useful.',
        tags: ['Data Classification', 'Insight Generation', 'Transformation', 'Big Data'],
    },
];

const agenticFeatures = [
    {icon: 'fa-sitemap',    delay: '',   title: 'Multi-Agent Orchestration via MCP',  desc: 'Specialised agents collaborate on complex tasks. MCP coordinates them into one seamless experience for your team.'},
    {icon: 'fa-user-check', delay: 'd1', title: 'Human-in-the-Loop at Every Step',    desc: 'AI handles routine work autonomously. When judgment is needed, control transfers instantly — one tap to exactly the right screen.'},
    {icon: 'fa-microphone', delay: 'd2', title: 'Voice-First, No Training Needed',    desc: 'Natural voice or text — no learning curve, no new UI to adopt. Works the way people actually communicate.'},
];

const agenticVerticals = [
    'FinTech & Wealth Management', 'Healthcare & Life Sciences',
    'Customer Success', 'Sales Operations', 'HR & Onboarding', 'Legal & Compliance',
];

const flowSteps = [
    {num: '01', title: 'Speak or Type',      desc: 'Voice or text query — no structured commands, just natural language.'},
    {num: '02', title: 'Intent Captured',    desc: 'AI understands your goal and builds an execution plan, broken into tasks for specialised agents.'},
    {num: '03', title: 'Agents Execute',     desc: 'Specialised agents complete the task — querying data, calling APIs, updating records, generating reports.'},
    {num: '04', title: 'You Stay in Control', desc: 'Take over anytime. Deep-links navigate you to exactly the right screen in your existing app.'},
    {num: '05', title: 'Insight Delivered',  desc: 'Answer, report, or completed action returned instantly, in the format you need.'},
];

const whyCards = [
    {num: '01', delay: '',   title: 'Production-Proven, Not Just PoC',    desc: 'Every project we take on is built to go live. Our track record includes solutions running in production for over two years, handling real data at scale.'},
    {num: '02', delay: 'd1', title: 'Honest AI Engineering',               desc: "We discard approaches that don't work — even when we've invested in them. Guardrails, fallbacks, and human oversight are built in from day one."},
    {num: '03', delay: 'd2', title: 'Regulated-Data Aware',                desc: 'With deep roots in FinTech and Life Sciences, we design AI systems that respect compliance requirements and protect sensitive data.'},
    {num: '04', delay: 'd3', title: 'Fast Delivery, Senior Engineers',     desc: 'No large account teams or handoffs. You work directly with experienced engineers who understand both business problems and AI implementation.'},
    {num: '05', delay: 'd4', title: 'Full-Stack AI Coverage',              desc: 'From document extraction to speech, video, and autonomous agents — we cover the full AI capability stack across all five pillars.'},
    {num: '06', delay: 'd5', title: 'Built for Startups & Mid-Market',     desc: 'Enterprise-grade AI without enterprise pricing. Our engagement model is right-sized for companies ready to move fast.'},
];

/* ─── Component ──────────────────────────────── */
function AiCapabilities() {

    useEffect(() => {
        window.scrollTo(0, 0);

        const io = new IntersectionObserver(entries => {
            entries.forEach(e => {
                if (e.isIntersecting) {
                    e.target.classList.add('visible');
                    io.unobserve(e.target);
                }
            });
        }, {threshold: 0.08});

        document.querySelectorAll('.ai-fade-up').forEach(el => io.observe(el));
        return () => io.disconnect();
    }, []);

    /* ── Section 1: Hero ─────────────────────── */
    const renderHero = () => (
        <div className="ai-hero">
            <div className="ai-hero-inner">
                <div className="ai-hc-layout">
                    <div>
                        <div className="ai-hc-eyebrow">AI Capabilities</div>
                        <h1 className="ai-hc-opening">
                            Your business challenge.<br />
                            <span>Our AI. Built to ship.</span>
                        </h1>
                        <p className="ai-hc-sub">
                            We start with your problem, not a technology pitch.
                            Then we build AI that goes live — and stays live.
                            No demos that disappear. No junior handoffs.
                        </p>
                        <div className="ai-hc-actions">
                            <Link to="/contact-us" className="ai-btn-primary">
                                <i className="fas fa-handshake"/> Let's Talk
                            </Link>
                        </div>
                    </div>
                    <div className="ai-hc-steps">
                        {heroSteps.map(({num, text, sub}) => (
                            <div key={num} className="ai-hc-step">
                                <span className="ai-hc-step-num">{num}</span>
                                <div>
                                    <div className="ai-hc-step-text">{text}</div>
                                    <div className="ai-hc-step-sub">{sub}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );

    /* ── Section 2: Five AI Pillars ─────────── */
    const renderPillars = () => (
        <section className="ai-body-section ai-pillars-section">
            <div className="ai-section-inner">
                <div className="ai-section-header ai-fade-up">
                    <div className="ai-section-tag">What We Build With</div>
                    <h2 className="ai-section-title">Five AI Capability <span>Pillars</span></h2>
                    <p className="ai-section-sub">
                        Our work spans text, vision, speech, video, and autonomous agents — a full AI stack
                        we draw from based on what your business problem demands.
                    </p>
                </div>
                <div className="ai-pillars-grid">
                    {pillars.map(({icon, delay, title, desc, tags}) => (
                        <div key={title} className={`ai-pillar-card ai-fade-up${delay ? ' ' + delay : ''}`}>
                            <div className="ai-pillar-icon"><i className={`fas ${icon}`}></i></div>
                            <h3>{title}</h3>
                            <p>{desc}</p>
                            <div className="ai-pillar-tags">
                                {tags.map(tag => <span key={tag} className="ai-pillar-tag">{tag}</span>)}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );

    /* ── Section 3: Agentic AI ───────────────── */
    const renderAgenticAI = () => (
        <section className="ai-body-section ai-agentic-section">
            <div className="ai-section-inner">
                <div className="ai-agentic-layout">
                    <div>
                        <div className="ai-agentic-new-pill ai-fade-up">
                            <span className="ai-pulse-dot"></span> New Capability
                        </div>
                        <h2 className="ai-section-title ai-fade-up">
                            Agentic AI —<br /><span>Your Always-On Assistant</span>
                        </h2>
                        <p className="ai-section-sub ai-fade-up">
                            Conversational AI that doesn't just answer questions — it takes action. Our Agentic
                            AI framework is a general-purpose offering we adapt to any business vertical. Your
                            team speaks or types; AI plans and executes; you stay in control at every step.
                        </p>
                        <div className="ai-agentic-features">
                            {agenticFeatures.map(({icon, delay, title, desc}) => (
                                <div key={title} className={`ai-agentic-feat ai-fade-up${delay ? ' ' + delay : ''}`}>
                                    <div className="ai-af-icon"><i className={`fas ${icon}`}></i></div>
                                    <div>
                                        <h4>{title}</h4>
                                        <p>{desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="ai-fade-up d3">
                            <div className="ai-uc-label">Applicable across verticals</div>
                            <div className="ai-uc-chips">
                                {agenticVerticals.map(v => (
                                    <span key={v} className="ai-uc-chip">{v}</span>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="ai-fade-up d1">
                        <div className="ai-flow-box">
                            <div className="ai-flow-title">How Agentic AI Works</div>
                            {flowSteps.map(({num, title, desc}) => (
                                <div key={num} className="ai-flow-step">
                                    <div className="ai-flow-num">{num}</div>
                                    <div className="ai-flow-content">
                                        <h4>{title}</h4>
                                        <p>{desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );

    /* ── Section 4: Why DeccanLogic ─────────── */
    const renderWhyUs = () => (
        <section className="ai-body-section ai-why-section">
            <div className="ai-section-inner">
                <div className="ai-section-header ai-fade-up">
                    <div className="ai-section-tag">Why Work With Us</div>
                    <h2 className="ai-section-title">AI Built for <span>Real Business Outcomes</span></h2>
                    <p className="ai-section-sub">
                        A small, senior team. Experienced engineers and honest delivery — not junior staff
                        with a shiny pitch.
                    </p>
                </div>
                <div className="ai-why-grid">
                    {whyCards.map(({num, delay, title, desc}) => (
                        <div key={num} className={`ai-why-card ai-fade-up${delay ? ' ' + delay : ''}`}>
                            <div className="ai-why-num">{num}</div>
                            <h3>{title}</h3>
                            <p>{desc}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );

    /* ── Section 5: CTA Banner ───────────────── */
    const renderCTA = () => (
        <section className="ai-cta-banner">
            <div className="ai-cta-inner">
                <div className="ai-cta-text ai-fade-up">
                    <h2>Ready to Explore AI for Your Business?</h2>
                    <p>
                        Let's talk about the problem you're trying to solve. We'll tell you honestly whether
                        AI is the right tool — and if it is, how we'd build it.
                    </p>
                </div>
                <div className="ai-cta-actions ai-fade-up d2">
                    <Link to="/contact-us" className="dl-btn dl-btn-white">
                        <i className="fas fa-envelope"></i> info@deccanlogic.com
                    </Link>
                </div>
            </div>
        </section>
    );

    /* ── Render ──────────────────────────────── */
    return (
        <div className="w-100">
            {renderHero()}
            {renderPillars()}
            {renderAgenticAI()}
            {renderWhyUs()}
            {renderCTA()}
            <Footer/>
        </div>
    );
}

AiCapabilities.propTypes = {};

AiCapabilities.defaultProps = {};

const mapStateToProps = (state) => ({
    isMobileView: state.appState.deviceInfo.isMobileView,
    isLGView: state.appState.deviceInfo.isLGView,
    isXLView: state.appState.deviceInfo.isXLView,
    isXSView: state.appState.deviceInfo.isXSView,
    isSMView: state.appState.deviceInfo.isSMView,
});

export default connect(mapStateToProps, {})(withTranslation('translations')(AiCapabilities));
